/**
 * Anvil Core
 *
 * Sets up component, partial and block initialization on the front-end and in the admin.
 *
 * Do not edit this file.
 * Do not import this file. An instance of this file is already available in the global scope.
 *
 * @package Anvil
 * @returns {Readonly<{init, component, setup, block, partial}>}
 */
function buildAnvilCore() {
    const modules = {
        components: {},
        blocks: {},
        partials: {},
    };

    return Object.freeze({
        component,
        block,
        partial,
        init,
        setup,
    });

    /**
     * Register a component.
     *
     * @param {String} name
     * @param {Function} callback
     */
    function component(name, callback) {
        modules.components[name] = callback;
    }

    /**
     * Register a block.
     *
     * @param {String} name
     * @param {Function} callback
     */
    function block(name, callback) {
        modules.blocks[name] = callback;
    }

    /**
     * Register a partial.
     *
     * @param {String} name
     * @param {Function} callback
     */
    function partial(name, callback) {
        modules.partials[name] = callback;
    }

    /**
     * Get the data from an element and clean up attributes.
     *
     * @param {HTMLElement} element
     * @param {String} type
     *
     * @returns {any}
     * @private
     */
    function _getFieldsAndCleanUp(element, type) {
        let data = null;

        if (typeof element.dataset.fields !== 'undefined') {
            // Convert from Base64 to JSON.
            data = JSON.parse(atob(element.dataset.fields));
        }

        // Remove attributes to prevent the component from being initialized twice.
        element.removeAttribute(`data-${type}`);

        if (element.hasAttribute('data-fields')) {
            element.removeAttribute('data-fields');
        }

        return data;
    }

    /**
     * Initialize a block.
     *
     * @param {String} name
     * @param {String} id
     * @param {Object|null} data
     *
     * @returns {Element|null}
     * @private
     */
    function _initBlockById(name, id, data = null) {
        // Attempt to find the HTML instance of the block.
        const instance = document.querySelector(`[data-block="${name}"][data-block-id="${id}"]`);
        if (!instance) return null;

        // Run the block's callback, if there is one.
        const callback = modules.blocks[name];
        if (callback) callback(instance, data);

        return instance;
    }

    /**
     * Initialize all blocks on the frontend.
     *
     * @private
     */
    function _initFrontendBlocks() {
        for (const [name, callback] of Object.entries(modules.blocks)) {
            const instances = document.querySelectorAll(`[data-block="${name}"]`);
            instances.forEach(i => {
                const blockId = i.dataset.blockId || '';
                const data = blockId && typeof window[blockId] !== 'undefined' ? window[blockId].data : null;

                if (callback) callback(i, data);
            });
        }
    }

    /**
     * Initialize all registered components.
     *
     * @param {Element|null} scope
     *
     * @private
     */
    function _initComponents(scope = null) {
        if (!scope) scope = document;

        // Attempt to find the HTML instances of the component.
        for (const [name, callback] of Object.entries(modules.components)) {
            const instances = scope.querySelectorAll(`[data-component="${name}"]`);
            instances.forEach(i => callback(i, _getFieldsAndCleanUp(i, 'component')));
        }
    }

    /**
     * Initialize all registered partials.
     *
     * @param {Element|null} scope
     *
     * @private
     */
    function _initPartials(scope = null) {
        if (!scope) scope = document;

        // Attempt to find the HTML instances of the partial.
        for (const [name, callback] of Object.entries(modules.partials)) {
            const instances = scope.querySelectorAll(`[data-partial="${name}"]`);
            instances.forEach(i => callback(i, _getFieldsAndCleanUp(i, 'partial')));
        }
    }

    /**
     * Initialize all registered components and blocks.
     */
    function init() {
        _initFrontendBlocks();
        _initComponents();
        _initPartials();
    }

    /**
     * Set up the AnvilCore object.
     */
    function setup() {
        if (typeof window.hammer !== 'undefined' && window.hammer.isAdmin) {
            window.hammer.onInitBlock((name, id, data = null) => {
                const blockElement = _initBlockById(name, id, data);
                if (blockElement) _initComponents(blockElement);
                if (blockElement) _initPartials(blockElement);
            });
        } else {
            window.addEventListener('DOMContentLoaded', init);
        }
    }
}

window.anvil = buildAnvilCore();
window.anvil.setup();

export default window.anvil;
